import React from 'react';

const useThrottled = (
    fn: (...args: any[]) => void,
    timeoutMs: number,
    { leading = true, trailing = true } = {},
): ((...args: any[]) => void) => {
    const timeoutRef = React.useRef<number | null>(null);
    const lastCallTimeRef = React.useRef<number | null>(null);
    const lastInvokeTimeRef = React.useRef<number | null>(null);

    React.useEffect(() => {
        return function cleanup() {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const shouldInvoke = React.useCallback(
        (time: number) => {
            // This function is borrowed from lodash.debounce (the other code is kinda dodgy)
            // https://github.com/lodash/lodash/blob/2da024c3b4f9947a48517639de7560457cd4ec6c/debounce.js#L136-L145

            const lastCallTime = lastCallTimeRef.current;
            const lastInvokeTime = lastInvokeTimeRef.current;
            const timeSinceLastCall = time - (lastCallTime ?? 0);
            const timeSinceLastInvoke = time - (lastInvokeTime ?? 0);

            // Either this is the first call, activity has stopped and we're at the
            // trailing edge, the system time has gone backwards and we're treating
            // it as the trailing edge, or we've hit the `maxWait` limit.
            return (
                lastCallTime === null ||
                timeSinceLastCall >= timeoutMs ||
                timeSinceLastCall < 0 ||
                timeSinceLastInvoke >= timeoutMs
            );
        },
        [timeoutMs],
    );

    const throttled = React.useCallback(
        (...args: any[]): void => {
            const time = Date.now();
            const isInvoking = shouldInvoke(time);
            lastCallTimeRef.current = time;

            if (timeoutRef.current) {
                return;
            }

            if (leading && isInvoking) {
                lastInvokeTimeRef.current = time;
                fn(...args);
            }

            timeoutRef.current = window.setTimeout(() => {
                timeoutRef.current = null;
                const time = Date.now();
                if (trailing && shouldInvoke(time)) {
                    lastInvokeTimeRef.current = time;
                    fn(...args);
                }
            }, timeoutMs);
        },
        [fn, leading, shouldInvoke, timeoutMs, trailing],
    );

    return throttled;
};

export default useThrottled;
